import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import Loader from './components/Loader/Loader';
import AdminLayout from './layouts/AdminLayout';

import GuestGuard from './components/Auth/GuestGuard';
import AuthGuard from './components/Auth/AuthGuard';

import { BASE_URL } from './config/constant';

export const renderRoutes = (routes = []) => (
    <Suspense fallback={<Loader />}>
        <Switch>
            {routes.map((route, i) => {
                const Guard = route.guard || Fragment;
                const Layout = route.layout || Fragment;
                const Component = route.component;

                return (
                    <Route
                        key={i}
                        path={route.path}
                        exact={route.exact}
                        render={(props) => (
                            <Guard>
                                <Layout>{route.routes ? renderRoutes(route.routes) : <Component {...props} />}</Layout>
                            </Guard>
                        )}
                    />
                );
            })}
        </Switch>
    </Suspense>
);

const routes = [
    {
        exact: true,
        guard: GuestGuard,
        path: '/auth/signin',
        component: lazy(() => import('./views/auth/signin/SignIn1'))
    },
    {
        exact: true,
        guard: GuestGuard,
        path: '/auth/signup',
        component: lazy(() => import('./views/auth/signup/SignUp1'))
    },
    {
        path: '*',
        layout: AdminLayout,
        guard: AuthGuard,
        routes: [
            {
                exact: true,
                path: '/dashboard',
                component: lazy(() => import('./views/dashboard/DashDefault'))
            },
            {
                exact: true,
                path: '/patients',
                component: lazy(() => import('./views/patients/Patients'))
            },
            {
                exact: true,
                path: '/schedule',
                component: lazy(() => import('./views/patients/Schedule'))
            },
            {
                exact: true,
                path: '/timings',
                component: lazy(() => import('./views/patients/DailySchedule'))
            },
            {
                exact: true,
                path: '/tasks',
                component: lazy(() => import('./views/tasks/PatientTasks'))
            },
            {
                exact: true,
                path: '/customer/:customerId/playing-history',
                component: lazy(() => import('./views/patients/PlayingHistory'))
            },
            {
                exact: true,
                path: '/patients/:patientId',
                component: lazy(() => import('./views/patients/PatientDetail'))
            },
            {
                exact: true,
                path: '/patients/:patientId/medicalHistory',
                component: lazy(() => import('./views/patients/MedicalHistory'))
            },
            {
                exact: true,
                path: '/patients/:patientId/medicalHistory/comorbidities',
                component: lazy(() => import('./views/patients/medicalHistory/ComorbiditiesPage'))
            },
            {
                exact: true,
                path: '/patients/:patientId/medicalHistory/surgical-history',
                component: lazy(() => import('./views/patients/medicalHistory/SurgicalHistoryPage'))
            },
            {
                exact: true,
                path: '/patients/:patientId/medicalHistory/personal-history',
                component: lazy(() => import('./views/patients/medicalHistory/PersonalHistoryPage'))
            },
            {
                exact: true,
                path: '/patients/:patientId/medicalHistory/medication-history',
                component: lazy(() => import('./views/patients/medicalHistory/MedicationHistoryPage'))
            },
            {
                exact: true,
                path: '/patients/:patientId/careplan',
                component: lazy(() => import('./views/patients/CarePlan'))
            },
            {
                exact: true,
                path: '/patients/:patientId/careplan/procedure',
                component: lazy(() => import('./views/patients/Procedure'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory',
                component: lazy(() => import('./views/patients/SpecificHistory'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/facialPain',
                component: lazy(() => import('./views/patients/specificHistories/FacialPain'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/fibromyalgia',
                component: lazy(() => import('./views/patients/specificHistories/Fibromyalgia'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/neuropathicpain',
                component: lazy(() => import('./views/patients/specificHistories/NeuroPathicPain'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/rheumatoid',
                component: lazy(() => import('./views/patients/specificHistories/RheumatoidArthritis'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/headache',
                component: lazy(() => import('./views/patients/specificHistories/Headache'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/abdominal',
                component: lazy(() => import('./views/patients/specificHistories/AbdominalPain'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/specificHistory/pudental',
                component: lazy(() => import('./views/patients/specificHistories/Pudendal'))
            },
            {
                exact: true,
                path: '/patients/:patientId/investigations',
                component: lazy(() => import('./views/patients/Investigation'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory',
                component: lazy(() => import('./views/patients/PainHistoryDetail'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/interference',
                component: lazy(() => import('./views/patients/Interference'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/interference/catascales',
                component: lazy(() => import('./views/patients/interferences/CataScales'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/interference/catascales/hads',
                component: lazy(() => import('./views/patients/interferences/HADS'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/interference/catascales/ptsd',
                component: lazy(() => import('./views/patients/interferences/PTSD'))
            },
            {
                exact: true,
                path: '/patients/:patientId/painHistory/interference/pdi',
                component: lazy(() => import('./views/patients/interferences/PDI'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination',
                component: lazy(() => import('./views/patients/Examination'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination/elbow',
                component: lazy(() => import('./views/patients/exams/ElbowExam'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination/footankle',
                component: lazy(() => import('./views/patients/exams/FootAnkle'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination/handwrist',
                component: lazy(() => import('./views/patients/exams/HandWrist'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination/hip',
                component: lazy(() => import('./views/patients/exams/Hip'))
            },
            {
                exact: true,
                path: '/patients/:patientId/examination/knee',
                component: lazy(() => import('./views/patients/exams/KneeJoint'))
            },
            {
                exact: true,
                path: '/customer/:customerId/buy-sell-transactions',
                component: lazy(() => import('./views/patients/BuySellTransactions'))
            },
            {
                exact: true,
                path: '/admin-transactions',
                component: lazy(() => import('./views/Transactions/AdminTransactions'))
            },
            {
                exact: true,
                path: '/refund-requests',
                component: lazy(() => import('./views/Refunds/RefundRequests'))
            },
            {
                exact: true,
                path: '/customer-transactions',
                component: lazy(() => import('./views/Transactions/CustomerTransactions'))
            },
            {
                exact: true,
                path: '/customers/:id',
                component: lazy(() => import('./views/patients/PatientDetail'))
            },
            {
                exact: true,
                path: '/games-report',
                component: lazy(() => import('./views/gameReport/index'))
            },
            {
                path: '*',
                exact: true,
                component: () => <Redirect to={BASE_URL} />
            }
        ]
    }
];

export default routes;
